import React from 'react';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import {Formik} from 'formik';
import {useSnackbar} from 'notistack';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import useTitle from '../../hooks/useTitle';
import {useReportPack} from './useReportPack';
import {formatBytes} from '../../utils/formatBytes';
import {handleFileEvent, toBase64} from '../ClientDocumentsPageV2/utilsV2';
import {
  AddGenericDocumentFormType,
  documentVersionField,
  reportPackDocumentTitleField,
} from './ReportPackFields';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import DocumentRow from '../../components/DocumentRow/DocumentRow';

const initialValues: AddGenericDocumentFormType = {
  files: undefined,
  documentTitle: '',
  version: '',
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const ManageDisclosuresPage = () => {
  useTitle('Manage Disclosure Docs');
  const {
    genericDocuments,
    loadingGenericDocuments,
    onUploadGenericDocument,
    refetchGenericDocuments,
    disableDocument,
    getDocumentUrl,
  } = useReportPack();

  const {enqueueSnackbar} = useSnackbar();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleToggleDocument = async (docId: string, newValue: boolean) => {
    const res = await disableDocument({
      variables: {genericDocumentId: docId},
    });
    const success = res.data?.disableGenericDocument?.ok;
    if (success) {
      enqueueSnackbar(newValue ? 'Document enabled' : 'Document disabled', {
        variant: 'success',
      });
      refetchGenericDocuments?.();
    } else {
      enqueueSnackbar('Failed to update document status', {
        variant: 'error',
      });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, {resetForm}) => {
        if (values.files?.length) {
          const file = values.files[0];
          const fileData = await toBase64(file);
          enqueueSnackbar(`Uploading ${file.name}...`, {variant: 'info'});

          const res = await onUploadGenericDocument({
            variables: {
              fileData,
              fileName: file.name,
              title: values.documentTitle,
              version: values.version,
            },
          });

          if (res.data?.uploadGenericDocument?.ok) {
            enqueueSnackbar('Uploaded file successfully', {
              variant: 'success',
            });
            resetForm();
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            refetchGenericDocuments?.();
          } else {
            enqueueSnackbar(
              `Upload failed: ${res.data?.uploadGenericDocument?.errors?.join(', ')}`,
              {variant: 'error'}
            );
          }
        }
      }}
    >
      {({setFieldValue, handleSubmit, values}) => {
        const file = values.files?.[0];
        const fileTooBig = file && file.size > MAX_FILE_SIZE;

        const activeDocs = (genericDocuments ?? []).filter(
          (doc): doc is NonNullable<typeof doc> => !!doc?.current
        );

        const inactiveDocs = (genericDocuments ?? []).filter(
          (doc): doc is NonNullable<typeof doc> => doc != null && !doc.current
        );

        return (
          <form onSubmit={handleSubmit}>
            <Panel
              title="Manage Disclosure Docs"
              style={{height: '100%'}}
              color={themeColors.amber}
            >
              <Box maxWidth="400px">
                {file && (
                  <>
                    <AutoField field={reportPackDocumentTitleField} />
                    <Box mt={2}>
                      <AutoField field={documentVersionField} />
                    </Box>
                    {(!values.documentTitle || !values.version) && (
                      <Typography color="error" fontSize={12} mt={1}>
                        Document Title and Version are required to upload
                      </Typography>
                    )}
                    <Button
                      disabled={fileTooBig || !values.documentTitle || !values.version}
                      variant="contained"
                      color="primary"
                      onClick={() => handleSubmit()}
                      sx={{mt: 2}}
                    >
                      Upload Document
                    </Button>
                    <Typography mt={1} fontSize={12}>
                      {file.name} – {formatBytes(file.size)}
                    </Typography>
                    {fileTooBig && <Typography color="error">File too large</Typography>}
                  </>
                )}
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    mt: 3,
                    backgroundColor: '#1D3557',
                    color: 'white',
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: '#16324c',
                    },
                  }}
                >
                  Add Generic Disclosure Document
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept="application/pdf"
                    onChange={(event) => handleFileEvent({event, setFieldValue})}
                    data-testid="file-input"
                  />
                </Button>
                <Typography mt={1} fontSize={10} display="block">
                  (Max file size: 10mb)
                </Typography>
              </Box>
              {loadingGenericDocuments ? (
                <CircularProgress />
              ) : (
                <>
                  <Accordion defaultExpanded sx={{mt: 4}}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Enabled Documents</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={2}
                        justifyContent="flex-start"
                      >
                        {activeDocs.map((gd) => (
                          <Box key={gd.id} flexBasis={{xs: '100%', sm: '48%'}}>
                            <DocumentRow
                              title={gd.title ?? ''}
                              version={gd.version ?? '1.0'}
                              isVisible={gd.current ?? false}
                              onToggleVisibility={(val) =>
                                handleToggleDocument(gd.id, val)
                              }
                              onView={async () => {
                                const res = await getDocumentUrl({
                                  variables: {genericDocumentId: gd.id},
                                });
                                const url = res.data?.getGenericDocumentUrl;
                                if (url) window.open(url, '_blank');
                                else
                                  enqueueSnackbar('Unable to fetch document URL', {
                                    variant: 'error',
                                  });
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{mt: 4}}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Disabled Documents</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={2}
                        justifyContent="flex-start"
                      >
                        {inactiveDocs.map((gd) => (
                          <Box key={gd.id} flexBasis={{xs: '100%', sm: '48%'}}>
                            <DocumentRow
                              title={gd.title ?? ''}
                              version={gd.version ?? '1.0'}
                              isVisible={gd.current ?? false}
                              onToggleVisibility={(val) => {
                                if (gd.current && !val) {
                                  handleToggleDocument(gd.id, val);
                                }
                              }}
                              onView={async () => {
                                const res = await getDocumentUrl({
                                  variables: {genericDocumentId: gd.id},
                                });
                                const url = res.data?.getGenericDocumentUrl;
                                if (url) window.open(url, '_blank');
                                else
                                  enqueueSnackbar('Unable to fetch document URL', {
                                    variant: 'error',
                                  });
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
            </Panel>
          </form>
        );
      }}
    </Formik>
  );
};
